/**
 * I percorsi dell'applicazione, in un posto solo.
 *
 * Ogni risorsa ha un URL: si può mandare a un collega il link di un set o di
 * un progetto, metterlo fra i preferiti, ricaricare con `F5` senza perdere la
 * posizione e usare il tasto «indietro» del browser senza uscire
 * dall'applicazione.
 *
 * I percorsi sono in italiano perché li legge l'utente, e restano gli stessi in
 * ogni lingua: un link condiviso non cambia con la lingua di chi lo apre. Gli
 * identificativi restano quelli del backend.
 *
 * Dal 2026-09-26 (ADR 0008 §11) il set è la risorsa di primo livello; dal
 * 2026-10-02 (ADR 0023 §3) lo è per ogni utente.
 *
 * Dal 2026-09-26 (ADR 0010 §8) i progetti si aprono dalla scheda Progetti del
 * set. Le pagine `/progetti/:id/...` restano raggiungibili per indirizzo.
 *
 * Dal 2026-10-05 (ADR 0030) la barra laterale ha il **set corrente** e le sue
 * sezioni (`setTabs`), la pagina iniziale è la Panoramica, l'amministrazione
 * sta sotto `/amministrazione` e gli indirizzi di prima (`legacyPaths`,
 * `legacySetTabs`) restano come rimbalzi.
 */

/** Percorsi senza parametri. */
export const paths = {
  /** Radice: rimanda alla pagina iniziale (`homeFor`). */
  root: '/',
  /** Pagina di accesso. */
  login: '/accesso',
  /** Password dimenticata: raggiungibile senza sessione (ADR 0030 §6.4). */
  forgotPassword: '/password-dimenticata',
  /** Registrazione della verifica in due passaggi (QR). */
  mfaEnroll: '/mfa/registrazione',
  /** Verifica in due passaggi. */
  mfaVerify: '/mfa/verifica',
  /** Codici di recupero, mostrati una sola volta. */
  recoveryCodes: '/mfa/codici',
  /** Panoramica: ciò che chiede un intervento e i set (ADR 0030 §3). */
  overview: '/panoramica',
  /** Tutti i set di agenti. */
  sets: '/set',
  /** Procedura di creazione di un set. */
  newSet: '/set/nuovo',
  /** Cronologia unica dei messaggi (ADR 0030 §3, tappa 8). */
  history: '/cronologia',
  /** Task di tutti i set, con le proposte in cima (ADR 0032, D3-B di ADR 0030). */
  allTasks: '/task',
  /** Calendario delle scadenze dei task (ADR 0032). */
  calendar: '/calendario',
  /** Domande frequenti e modulo per gli amministratori (ADR 0032). */
  help: '/aiuto',
  /** Pagine di un progetto (`/progetti/:id/...`); l'elenco è passato in Amministrazione. */
  projects: '/progetti',
  /** Strumenti collegati dall'utente. */
  tools: '/strumenti',
  /** Template di sito pubblicati in anteprima, per ogni utente autenticato. */
  templates: '/template',
  /** InstitorAI DevHub: app desktop compresa nel Premium, download e chiave di licenza. */
  devhub: '/devhub',
  /** Abbonamento, consumo, ricarica dei minuti di voce e pagamenti (ADR 0027, D5). */
  billing: '/abbonamento',
  /** Consumo in crediti e dettaglio dei token (ADR 0063). */
  consumption: '/consumo',
  /** Ritorno dal Checkout di Stripe: sincronizza la sessione e mostra l'esito. */
  billingReturn: '/abbonamento/esito',
  /** Profilo e sicurezza, dal menu utente. */
  profile: '/profilo',
  /** Impostazioni di questo browser, notifiche e privacy, dal menu utente. */
  settings: '/impostazioni',
  /** Area di amministrazione della piattaforma (solo `ADMIN`). */
  admin: '/amministrazione',
  /** Utenti della piattaforma. */
  adminUsers: '/amministrazione/utenti',
  /** Elenco di tutti i progetti. */
  adminProjects: '/amministrazione/progetti',
  /** Account Google e GitHub di institor. */
  adminPlatform: '/amministrazione/piattaforma',
  /** Grafana, Prometheus e allarmi su Telegram. */
  adminMonitoring: '/amministrazione/monitoraggio',
  /** Richieste di aiuto degli utenti (ADR 0032). */
  adminSupport: '/amministrazione/richieste',
  /** Codici regalo (ADR 0043). */
  adminGiftCodes: '/amministrazione/codici-regalo',
  /** Scadenze di token, chiavi API, prove e abbonamenti (ADR 0044). */
  adminDeadlines: '/amministrazione/scadenze',
  /** Listino: prezzi e limiti dei piani (ADR 0058). */
  adminPriceList: '/amministrazione/listino',
} as const;

/**
 * Indirizzi di prima di ADR 0030, che restano come rimbalzi (§2.4): i link
 * salvati continuano a funzionare.
 */
export const legacyPaths = {
  /** Piano e consumo: ora la sezione «Consumo» dell'abbonamento. */
  plan: '/piano',
  /** Utenti: ora in Amministrazione. */
  users: '/utenti',
  /** Catalogo dei ruoli: tolto il 2026-10-28, rimbalza sulla Panoramica. */
  agentRoles: '/ruoli-agenti',
  /** Catalogo dei ruoli dopo ADR 0030: tolto il 2026-10-28, rimbalza sulla Panoramica. */
  roles: '/ruoli',
  /** Monitoraggio: ora in Amministrazione. */
  monitoring: '/monitoraggio',
} as const;

/** Ancora della sezione «Consumo» nella pagina dell'abbonamento. */
export const USAGE_ANCHOR = 'consumo';

/** Ancora del grafico del consumo per giorno nella Panoramica. */
export const DAILY_USAGE_ANCHOR = 'consumo-giornaliero';

/** Ancora dei telefoni registrati nel profilo. */
export const PHONES_ANCHOR = 'telefoni';

/** Ancora del registro accessi nel profilo. */
export const ACCESS_LOG_ANCHOR = 'registro-accessi';

/**
 * Sceglie la pagina iniziale di un utente.
 *
 * Dal 2026-10-05 (ADR 0030, D2) è la Panoramica. Restano due eccezioni:
 * chi non ha set e può crearne uno parte dalla procedura, chi non può crearne
 * parte dagli strumenti, perché per loro la Panoramica sarebbe vuota.
 *
 * @param admin True per l'amministratore della piattaforma.
 * @param ownedSets Set posseduti; `null` se non noti.
 * @param canCreateSet True se il piano consente un altro set; `null` se non noto.
 * @returns Il percorso della pagina iniziale.
 */
export function homeFor(admin: boolean, ownedSets: number | null, canCreateSet: boolean | null): string {
  if (admin || (ownedSets !== null && ownedSets > 0) || canCreateSet === null) {
    return paths.overview;
  }
  return canCreateSet ? paths.newSet : paths.tools;
}

/**
 * Sezioni del set corrente, voci della barra laterale (ADR 0030 §2.1, D1).
 * Prima erano schede della pagina del set.
 */
export const setTabs = {
  /** Relay a tre colonne: richieste, conversazione, chi sta lavorando. */
  relay: 'relay',
  /** Task dei progetti del set: elenco, kanban, proposte (ADR 0029). */
  tasks: 'task',
  /** Siti dei progetti, da vedere e annotare (ADR 0018 §6). */
  artifacts: 'artifacts',
  /** Progetti del set. */
  projects: 'progetti',
  /** Membri e inviti del set (ADR 0032). */
  members: 'membri',
  /** Permessi del set: persone, progetti, collegamenti, limiti fissi (ADR 0041). */
  permissions: 'permessi',
  /** Credenziali, taglia, profilo, ciclo di vita. */
  settings: 'impostazioni',
} as const;

/** Chiave di una sezione del set. */
export type SetTab = (typeof setTabs)[keyof typeof setTabs];

/** Sezioni del set nell'ordine del menu. */
export const setTabOrder: SetTab[] = [
  setTabs.relay,
  setTabs.tasks,
  setTabs.artifacts,
  setTabs.projects,
  setTabs.members,
  setTabs.permissions,
  setTabs.settings,
];

/** Segmenti delle schede di prima di ADR 0030, che rimbalzano sul relay. */
export const legacySetTabs = {
  /** Conversazione: ora il centro del relay. */
  conversation: 'conversazione',
  /** Attività: ora la colonna «Chi sta lavorando» del relay, con il registro aperto. */
  activity: 'attivita',
  /** Agenti del set: tolta il 2026-10-28, gli agenti li gestisce l'Institore. */
  agents: 'agenti',
} as const;

/** Parametro dell'indirizzo dei permessi che porta alla scheda di un progetto (ADR 0041). */
export const PERMISSIONS_PROJECT_PARAM = 'progetto';

/**
 * Percorso della pagina dei permessi di un set, eventualmente su un progetto.
 *
 * @param setId Set.
 * @param projectId Progetto da mostrare; omesso, la pagina dall'inizio.
 * @returns Il percorso assoluto.
 */
export function setPermissionsPath(setId: string, projectId?: string): string {
  const base = setPath(setId, setTabs.permissions);
  return projectId === undefined ? base : `${base}?${PERMISSIONS_PROJECT_PARAM}=${encodeURIComponent(projectId)}`;
}

/** Parametro dell'indirizzo del relay che apre il registro di chi sta lavorando. */
export const RELAY_LOG_PARAM = 'registro';

/** Schede della pagina di un progetto, nell'ordine in cui compaiono. */
export const projectTabs = {
  /** Dati del progetto. */
  overview: 'panoramica',
  /** Strumenti collegati al progetto. */
  tools: 'strumenti',
  /** Dipendenze dichiarate verso altri progetti e da altri progetti. */
  links: 'dipendenze',
  /** Permessi concessi sul progetto. */
  grants: 'accessi',
  /** Requisiti funzionali, modifiche proposte e verifiche (ADR 0050 §6). */
  requirements: 'requisiti',
} as const;

/** Chiave di una scheda della pagina di un progetto. */
export type ProjectTab = (typeof projectTabs)[keyof typeof projectTabs];

/** Schede del progetto nell'ordine di presentazione. */
export const projectTabOrder: ProjectTab[] = [
  projectTabs.overview,
  projectTabs.requirements,
  projectTabs.tools,
  projectTabs.links,
  projectTabs.grants,
];

/**
 * Percorso della pagina di un progetto.
 *
 * @param projectId Identificativo del progetto.
 * @param tab Scheda da aprire; omessa, si apre la panoramica.
 * @returns Il percorso assoluto.
 */
export function projectPath(projectId: string, tab: ProjectTab = projectTabs.overview): string {
  return `${paths.projects}/${encodeURIComponent(projectId)}/${tab}`;
}

/**
 * Percorso della pagina di un set.
 *
 * @param setId Identificativo del set.
 * @param tab Sezione da aprire; omessa, il relay.
 * @returns Il percorso assoluto.
 */
export function setPath(setId: string, tab: SetTab = setTabs.relay): string {
  return `${paths.sets}/${encodeURIComponent(setId)}/${tab}`;
}

/** Vista della pagina di un sito negli Artefatti. */
export type ArtifactView = 'sito' | 'condividi' | 'info';

/** Viste della pagina di un sito, nell'ordine delle schede. */
export const artifactViews: readonly ArtifactView[] = ['sito', 'condividi', 'info'];

/** Parametro dell'indirizzo con la vista della pagina di un sito. */
export const ARTIFACT_VIEW_PARAM = 'vista';

/**
 * Percorso della pagina di un sito negli Artefatti: demo o produzione di un progetto.
 *
 * @param setId Set.
 * @param projectId Progetto.
 * @param environment `demo` o `prod`.
 * @param view Scheda da aprire; omessa, «Sito».
 * @returns Il percorso assoluto.
 */
export function artifactPath(
  setId: string,
  projectId: string,
  environment: 'demo' | 'prod',
  view?: ArtifactView,
): string {
  const base = `${setPath(setId, setTabs.artifacts)}/${encodeURIComponent(projectId)}/${environment}`;
  return view === undefined || view === 'sito' ? base : `${base}?${ARTIFACT_VIEW_PARAM}=${view}`;
}

/**
 * Riconosce la vista della pagina di un sito.
 *
 * @param value Valore del parametro `vista`.
 * @returns La vista; «sito» per un valore sconosciuto.
 */
export function artifactViewOf(value: string | null): ArtifactView {
  return artifactViews.find((view) => view === value) ?? 'sito';
}

/**
 * Percorso della pagina di gestione di una connessione Google (ADR 0011 §8).
 *
 * ADR 0011 la chiama `/tools/google/:connectionId`; qui segue la regola degli
 * indirizzi in italiano, sotto `/strumenti`, e il router rimanda il percorso
 * dell'ADR a questo.
 *
 * @param connectionId Identificativo della connessione.
 * @returns Il percorso assoluto.
 */
export function googleConnectionPath(connectionId: string): string {
  return `${paths.tools}/google/${encodeURIComponent(connectionId)}`;
}

/**
 * Riconosce la scheda di progetto indicata da un segmento di URL.
 *
 * Un segmento sconosciuto ricade sulla panoramica invece di produrre una rotta
 * vuota: un link scritto a mano male, o una scheda che non esiste più (come
 * «harness»), porta comunque a qualcosa di sensato.
 *
 * @param segment Segmento letto dall'URL.
 * @returns La scheda corrispondente.
 */
export function projectTabOf(segment: string | undefined): ProjectTab {
  return projectTabOrder.find((tab) => tab === segment) ?? projectTabs.overview;
}

/**
 * Riconosce la sezione di un set indicata da un segmento di URL.
 *
 * @param segment Segmento letto dall'URL.
 * @returns La sezione corrispondente; il relay per un segmento sconosciuto.
 */
export function setTabOf(segment: string | undefined): SetTab {
  return setTabOrder.find((tab) => tab === segment) ?? setTabs.relay;
}

/**
 * Ricava il set e la sezione da un percorso `/set/:id/:sezione`.
 *
 * `/set/nuovo` non è un set: è la procedura di creazione.
 *
 * @param pathname Percorso corrente.
 * @returns Identificativo e sezione, oppure `null` fuori dalle pagine di un set.
 */
export function setRouteOf(pathname: string): { setId: string; tab: SetTab } | null {
  const match = /^\/set\/([^/]+)(?:\/([^/]+))?/.exec(pathname);
  if (match === null || match[1] === undefined) {
    return null;
  }
  let setId: string;
  try {
    setId = decodeURIComponent(match[1]);
  } catch {
    return null;
  }
  if (setId === 'nuovo') {
    return null;
  }
  return { setId, tab: setTabOf(match[2]) };
}
